import type { LucideIcon } from "lucide-react";
import { ChevronDown, FileText, PiggyBank, Wallet } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/shared/lib/cn";
import { motionTokens, riseItem, springs, staggerContainer } from "@/shared/lib/motion";
import { AnimatedNumber } from "@/shared/ui/AnimatedNumber";
import type { DecisionDashboard } from "../../api/claimsApi";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const CURRENCY = "USD";
/** Nombre de mois affichés dans les barres d'une grande tuile. */
const BAR_MONTHS = 7;

type Formatter = (value: number | null | undefined) => string;

/** « 2026-10 » → « Oct ». */
function monthLabel(key: string): string {
  return MONTHS[Number(key.slice(5, 7)) - 1] ?? key;
}

/* ── Grandes tuiles (première rangée) ── */

type FeatureTone = "green" | "blue" | "orange";

const FEATURE_STYLES: Record<
  FeatureTone,
  { card: string; chip: string; label: string; value: string; sub: string; bar: string; track: string; footer: string; dot: string; tooltip: string }
> = {
  green: {
    card: "border-transparent bg-[linear-gradient(135deg,#0a4a31_0%,#0e6a40_50%,#15a34a_100%)] text-white",
    chip: "bg-accent text-accent-foreground",
    label: "text-white",
    value: "text-white",
    sub: "text-white/80",
    bar: "bg-[linear-gradient(180deg,#a6f21b,#22c55e)]",
    track: "bg-white/10",
    footer: "border-white/10 bg-black/15 text-white/90",
    dot: "bg-accent",
    tooltip: "bg-white text-[#0a4a31]",
  },
  blue: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#f4f8ff_55%,#e6effd_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#14283d_100%)]",
    chip: "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
    label: "text-foreground",
    value: "text-foreground",
    sub: "text-muted-foreground",
    bar: "bg-[linear-gradient(180deg,#3b82f6,#bfdbfe)]",
    track: "bg-blue-500/10",
    footer: "border-blue-100 bg-blue-50/80 text-muted-foreground dark:border-blue-500/10 dark:bg-blue-500/10",
    dot: "bg-blue-600",
    tooltip: "bg-[var(--brand-deep)] text-white",
  },
  orange: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#fff6ef_55%,#ffeadb_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#2e2219_100%)]",
    chip: "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300",
    label: "text-foreground",
    value: "text-foreground",
    sub: "text-muted-foreground",
    bar: "bg-[linear-gradient(180deg,#f97316,#fed7aa)]",
    track: "bg-orange-500/10",
    footer: "border-orange-100 bg-orange-50/80 text-muted-foreground dark:border-orange-500/10 dark:bg-orange-500/10",
    dot: "bg-orange-600",
    tooltip: "bg-[var(--brand-deep)] text-white",
  },
};

/** Barres mensuelles d'une grande tuile ; survol d'une barre : mois et montant. */
function MonthBars({
  values,
  monthKeys,
  endIndex,
  tone,
  formatValue,
  label,
}: {
  values: number[];
  monthKeys: string[];
  endIndex: number;
  tone: FeatureTone;
  formatValue: Formatter;
  label: string;
}) {
  const styles = FEATURE_STYLES[tone];
  const start = Math.max(0, endIndex - BAR_MONTHS + 1);
  const months = values.slice(start, endIndex + 1).map((value, i) => ({ value, month: monthLabel(monthKeys[start + i] ?? "") }));
  const max = Math.max(1, ...months.map((m) => m.value));

  return (
    <div className="flex h-[4.5rem] shrink-0 items-end gap-1.5" role="img" aria-label={label}>
      {months.map((m, index) => (
        <span key={m.month} className={cn("group/bar relative flex h-full w-2 items-end rounded-full", styles.track)}>
          <motion.span
            className={cn("w-full origin-bottom rounded-full", styles.bar)}
            style={{ height: `${(m.value / max) * 100}%` }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ ...springs.gentle, delay: 0.2 + index * motionTokens.stagger }}
          />
          <span
            className={cn(
              "pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg px-2 py-1 text-[11px] font-medium tabular-nums opacity-0 shadow-[var(--shadow-sm)] transition-opacity duration-150 group-hover/bar:opacity-100",
              styles.tooltip,
            )}
          >
            {m.month} · {formatValue(m.value)} {CURRENCY}
          </span>
        </span>
      ))}
    </div>
  );
}

function Amount({
  amount,
  format,
  className,
  unitClassName,
  unit = CURRENCY,
}: {
  amount: number | null | undefined;
  format: Formatter;
  className?: string;
  unitClassName?: string;
  unit?: string;
}) {
  return (
    <p className={cn("font-bold leading-none tracking-tight", className)}>
      {amount === null || amount === undefined ? (
        "—"
      ) : (
        <>
          <AnimatedNumber value={amount} format={format} />
          <span className={cn("ml-2 font-bold", unitClassName)}>{unit}</span>
        </>
      )}
    </p>
  );
}

function FeatureCard({
  tone,
  icon: Icon,
  label,
  amount,
  format,
  sub,
  footer,
  bars,
  action,
  className,
  unit,
}: {
  className?: string;
  unit?: string;
  tone: FeatureTone;
  icon: LucideIcon;
  label: string;
  amount: number;
  format: Formatter;
  sub: string;
  footer: string;
  bars?: React.ReactNode;
  action?: React.ReactNode;
}) {
  const styles = FEATURE_STYLES[tone];

  return (
    <motion.div
      variants={riseItem}
      whileHover={{ y: -motionTokens.distance.xs }}
      transition={springs.snappy}
      className={cn("relative isolate flex flex-col overflow-hidden rounded-[var(--radius-card)] border shadow-[var(--shadow-card)]", styles.card, className)}
    >
      {tone === "green" ? (
        // Reflets clairs en diagonale, comme la maquette.
        <span
          className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(115deg,transparent_45%,rgb(255_255_255/0.08)_55%,transparent_65%),radial-gradient(circle_at_85%_20%,rgb(166_242_27/0.25),transparent_45%)]"
          aria-hidden="true"
        />
      ) : null}
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-center justify-between gap-3">
          <dt className={cn("flex items-center gap-3 text-[15px] font-semibold", styles.label)}>
            <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", styles.chip)}>
              <Icon size={19} strokeWidth={2} aria-hidden="true" />
            </span>
            {label}
          </dt>
          {action}
        </div>
        <div className="flex items-end justify-between gap-4">
          <dd className="min-w-0">
            <Amount amount={amount} format={format} unit={unit} className={cn("text-[2rem]", styles.value)} unitClassName="text-xl" />
            <p className={cn("mt-2 text-sm", styles.sub)}>{sub}</p>
          </dd>
          {bars}
        </div>
      </div>
      <p className={cn("flex items-center gap-2 border-t px-5 py-3 text-xs font-medium", styles.footer)}>
        <span className={cn("h-2 w-2 shrink-0 rounded-full", styles.dot)} aria-hidden="true" />
        {footer}
      </p>
    </motion.div>
  );
}

/**
 * Indicateurs clés du compte Assurance : à payer, facturé et économies. Le prestataire choisi sur la première
 * carte filtre aussi le circuit (le tableau de bord se recharge).
 */
export function InsurerKpiGrid({
  dashboard,
  providerId,
  onProviderChange,
  formatMoney,
  formatPercent,
}: {
  dashboard: DecisionDashboard;
  providerId: string;
  onProviderChange: (value: string) => void;
  formatMoney: Formatter;
  formatPercent: Formatter;
}) {
  const { finance, monthly, invoices } = dashboard;
  const monthKeys = monthly.map((m) => m.month);
  const current = monthKeys.indexOf(invoices.current_month);
  const endIndex = current >= 0 ? current : monthKeys.length - 1;
  const billedThisMonth = monthly[endIndex]?.billed ?? 0;
  const plural = (count: number, word: string) => `${count} ${word}${count > 1 ? "s" : ""}`;

  return (
    <div className="flex flex-col gap-4">
      <motion.dl className="grid gap-4 md:grid-cols-3" variants={staggerContainer} initial="hidden" animate="visible">
        <FeatureCard
          tone="green"
          icon={Wallet}
          label="À payer aux prestataires"
          amount={finance.to_pay}
          format={formatMoney}
          sub={`${plural(invoices.validated, "facture")} ${invoices.validated > 1 ? "validées" : "validée"}`}
          footer={
            invoices.validated_this_month > 0
              ? `${plural(invoices.validated_this_month, "facture")} ${invoices.validated_this_month > 1 ? "validées" : "validée"} ce mois`
              : "Aucune facture validée ce mois"
          }
          action={
            <label className="relative shrink-0">
              <span className="sr-only">Prestataire</span>
              <select
                value={providerId}
                onChange={(e) => onProviderChange(e.target.value)}
                title="Filtre les cartes et le circuit SafeCheck"
                className="w-[7.5rem] appearance-none truncate rounded-lg border border-white/25 bg-white/5 py-1.5 pl-3 pr-8 text-xs font-medium text-white outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <option value="" className="text-foreground">Prestataire</option>
                {dashboard.providers.map((row) => (
                  <option key={row.id} value={row.id} className="text-foreground">
                    {row.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-white/80" aria-hidden="true" />
            </label>
          }
          bars={
            <MonthBars
              values={monthly.map((m) => m.validated)}
              monthKeys={monthKeys}
              endIndex={endIndex}
              tone="green"
              formatValue={formatMoney}
              label="Montant validé par mois"
            />
          }
        />
        <FeatureCard
          tone="blue"
          icon={FileText}
          label="Facturé"
          amount={monthly.reduce((sum, month) => sum + month.billed, 0)}
          format={formatMoney}
          sub={`${plural(invoices.received, "facture")} ${invoices.received > 1 ? "reçues" : "reçue"}`}
          footer={`${formatMoney(billedThisMonth)} ${CURRENCY} facturés ce mois`}
          bars={
            <MonthBars
              values={monthly.map((m) => m.billed)}
              monthKeys={monthKeys}
              endIndex={endIndex}
              tone="blue"
              formatValue={formatMoney}
              label="Montant facturé par mois"
            />
          }
        />
        <FeatureCard
          tone="orange"
          icon={PiggyBank}
          label="Économies (réfactions)"
          amount={finance.savings}
          format={formatMoney}
          sub={`Soit ${formatPercent(finance.savings_rate)} du montant facturé`}
          footer={`${formatMoney(finance.savings)} ${CURRENCY} d'économies réalisées`}
          bars={
            <MonthBars
              values={monthly.map((m) => Math.max(0, m.billed - m.validated))}
              monthKeys={monthKeys}
              endIndex={endIndex}
              tone="orange"
              formatValue={formatMoney}
              label="Écart entre facturé et validé par mois"
            />
          }
        />
      </motion.dl>
    </div>
  );
}
